import { ParsedUrlQuery } from 'querystring';

import React, { useEffect, useMemo, useRef, useState } from 'react';

import dynamic from 'next/dynamic';
import moment from 'moment';
import { useSearchParams } from 'next/navigation';

import {
  PackageDetailRoomViewModel,
  PackageTransferViewModel,
  RoomFeatureViewModel,
  RoomImageViewModel,
  RoomViewModel,
} from '@api/v3';
import { FlexibleDiv } from '@components/formStyles';
import { useSearchBarContext } from '@contexts/hotel/SearchBar';
import { Currencies } from '@constants/constants';
import { getCurrencySymbol } from '@utils/utils';
import { useDevice } from '@contexts/device';
import { SelectedVariationProps } from '@components/views/hotel/detail/context/types';
import { ModalView } from '@components/views/hotel/detail/components/details-galleries-modal';
import { RoomsVariationBarItem } from '@components/views/hotel/detail/components/rooms-variation-bar/Item/props';
import {
  useHotelDetailState,
  useSetSelectedVariations,
  useSetUnSelectedRooms,
} from '@components/views/hotel/detail/context';
import { FlightProps } from '@components/views/package/components/helper';
import { useGetHotelAvailableDates } from '@components/views/hotel/detail/components/room-selection/components/AvailableRoomDates/hooks';
import { URL_DATE_FORMAT, formatISOToDate } from '@utils/dateUtil';
import { paramsToObject } from '@components/views/utils';
import { logUnavailableProduct } from '@utils/gtm/unavailable-products-gtm';

import {
  Wrapper,
  CardContainer,
  ContainerRoomDetail,
  RoomsCardHeader,
} from './styled';
import RoomOptionDetails from './RoomOptionDetails';
import { HotelRooms } from '../../types';
import ShowMoreCard, { ArrowAlign } from '../package-show-more-card';
import AvailableRoomsFilter from '../available-rooms-filter';
import RoomsVariationsBar from '../rooms-variation-bar';

const PriceSheet = dynamic(
  () =>
    import('@components/views/hotel/detail/components/price-sheet/PriceSheet')
);

const NoAvailableRoomsBanner = dynamic(
  () =>
    import('@components/views/hotel/detail/components/NoAvailableRoomsBanner')
);
const DetailedRoomDesktop = dynamic(
  () =>
    import(
      '@components/views/hotel/detail/components/room-information/DetailedRoomDesktop'
    )
);
const DetailedRoomMobile = dynamic(
  () =>
    import(
      '@components/views/hotel/detail/components/room-information/DetailedRoomMobile'
    )
);

export interface DetailedRoomProps {
  type?: string;
  description?: string;
  roomTypeName?: string;
  roomId?: number;
  images: RoomImageViewModel[];
  features?: RoomFeatureViewModel[];
  hasCancellationHour?: boolean;
  showRoom: (view: ModalView) => void;
}

interface Props {
  items: HotelRooms[];
  hotelId: number;
  hotelName: string;
  nightCount: number;
  roomNumber: number;
  viewRoom: (
    room: RoomViewModel | PackageDetailRoomViewModel,
    view: ModalView
  ) => void;
  openDatePicker?: () => void;
  isExpedia: boolean;
  roomsVariationsItems?: RoomsVariationBarItem[];
  selectedTabPackage?: string;
  selectedDepatureFlight?: FlightProps;
  selectedReturnFlight?: FlightProps;
  selectedDepatureTransfer?: PackageTransferViewModel;
  selectedReturnTransfer?: PackageTransferViewModel;
  adultCount: number;
  childCount: number;
  country?: string;
}

const RoomOptions = ({
  items,
  hotelId,
  hotelName,
  nightCount,
  roomNumber = 1,
  viewRoom,
  openDatePicker,
  isExpedia,
  roomsVariationsItems = [],
  selectedTabPackage,
  selectedDepatureFlight,
  selectedReturnFlight,
  selectedDepatureTransfer,
  selectedReturnTransfer,
  adultCount,
  childCount = 0,
  country,
}: Props) => {
  const { isDesktop, isMobile } = useDevice();
  const [selectedTab, setSelectedTab] = useState(1);
  const searchParams = useSearchParams();
  const query: ParsedUrlQuery = useMemo(() => {
    return paramsToObject(searchParams?.entries());
  }, [searchParams]);

  const [selectedRoomVariations, setSelectedRoomVariations] =
    useState<RoomsVariationBarItem>(roomsVariationsItems?.[0]);

  const activeRoom = items?.find(
    (room) => room.roomTypeId === selectedRoomVariations?.roomId
  );

  const checkInDate_ = query.in
    ? formatISOToDate(query.in as string, URL_DATE_FORMAT)
    : moment().format(URL_DATE_FORMAT);

  const checkOutDate_ = query.out
    ? moment(query.out).add(2, 'months').format(URL_DATE_FORMAT)
    : moment().add(2, 'months').format(URL_DATE_FORMAT);

  const { availableDates, isFetching: isFetchingAvailableDates } =
    useGetHotelAvailableDates({
      roomId: activeRoom?.id ?? -1,
      runOnMount: true,
      checkInDate: checkInDate_,
      checkOutDate: checkOutDate_,
    });

  const setSelectedVariations = useSetSelectedVariations();
  const setUnSelectedRooms = useSetUnSelectedRooms();
  const { selectedVariations } = useHotelDetailState();
  const [onlyAvailableRooms, setOnlyAvailableRooms] = useState<boolean>(false);
  const isLogged = useRef(false);
  const {
    startDate,
    endDate,
    hotelRoomsList: hotelRoomList,
  } = useSearchBarContext();
  const [isPriceSheetVisible, setIsPriceSheetVisible] = useState(false);

  useEffect(() => {
    const activeTabs = new Array(roomNumber)
      .join()
      .split(',')
      .map((_, index) => ++index);

    const selectedRooms: number[] = selectedVariations?.map(
      (selected) => selected.tabNumber
    );

    const unSelectedTabs = activeTabs.filter(
      (tab) => !selectedRooms.includes(tab)
    );
    setUnSelectedRooms(unSelectedTabs);
    unSelectedTabs.length && setSelectedTab(unSelectedTabs[0]);
  }, [selectedVariations]);

  const hasNoAvailableRooms = useMemo(
    () => roomsVariationsItems.every((room) => !room.isAvailable),
    [roomsVariationsItems]
  );
  useEffect(() => {
    if (!checkInDate_ && !checkOutDate_) return;
    if (hasNoAvailableRooms && !isLogged.current) {
      logUnavailableProduct(
        hotelName,
        `${checkInDate_} - ${checkOutDate_}`,
        country === 'TR' ? 'Yurtiçi Otelleri' : 'Yurtdışı Otelleri'
      );
      isLogged.current = true;
    }
  }, [hasNoAvailableRooms]);
  const isSelectedRoom = (tabNumber: number) =>
    selectedVariations.some((selected) => selected.tabNumber === tabNumber);

  const handleSelectVariation = (variation: SelectedVariationProps): void => {
    if (isSelectedRoom(variation.tabNumber)) {
      setSelectedVariations([
        ...selectedVariations.filter(
          (selected) => selected.tabNumber !== variation.tabNumber
        ),
        variation,
      ]);
    } else {
      setSelectedVariations([...selectedVariations, variation]);
    }
  };

  const onClosePriceSheet = () => {
    setIsPriceSheetVisible(false);
  };
  const onOpenPriceSheet = () => {
    setIsPriceSheetVisible(true);
  };

  const toggleSelectedRoomVariations = (item: RoomsVariationBarItem) =>
    setSelectedRoomVariations(item);

  const handleFilterAvailableRooms = (checked: boolean) =>
    setOnlyAvailableRooms(checked);

  useEffect(() => {
    toggleSelectedRoomVariations(roomsVariationsItems?.[0]);
  }, [roomsVariationsItems]);

  const filteredRoomVariations = onlyAvailableRooms
    ? roomsVariationsItems.filter((item) => item.isAvailable)
    : roomsVariationsItems;

  return (
    <ContainerRoomDetail>
      <ShowMoreCard
        title='Oda Seçenekleri'
        navigationText='Tüm Sezon Fiyatlarını Göster'
        isShowAll
        onNavigate={!isExpedia ? onOpenPriceSheet : undefined}
        arrowAlign={ArrowAlign.FIXED_RIGHT}
        containerStyle={isMobile ? { padding: 0 } : undefined}
        provider={activeRoom?.provider}
      >
        {onlyAvailableRooms && hasNoAvailableRooms ? (
          <NoAvailableRoomsBanner />
        ) : (
          <>
            <RoomsCardHeader>
              <AvailableRoomsFilter
                onFilterChecked={handleFilterAvailableRooms}
              />
            </RoomsCardHeader>
            <Wrapper>
              {filteredRoomVariations?.length ? (
                <FlexibleDiv bottom={20}>
                  <RoomsVariationsBar
                    adultCount={adultCount}
                    childCount={childCount}
                    items={filteredRoomVariations}
                    activeItem={selectedRoomVariations}
                    toggleActiveItem={toggleSelectedRoomVariations}
                    selectedDepatureFlight={selectedDepatureFlight}
                    selectedReturnFlight={selectedReturnFlight}
                    selectedDepatureTransfer={selectedDepatureTransfer}
                    selectedReturnTransfer={selectedReturnTransfer}
                  />
                </FlexibleDiv>
              ) : null}
              {!isDesktop ? (
                <DetailedRoomMobile
                  provider={activeRoom?.provider}
                  type={activeRoom?.name}
                  description={activeRoom?.description || ''}
                  roomTypeName={activeRoom?.typeName || ''}
                  images={activeRoom?.images || []}
                  features={activeRoom?.features}
                  showRoom={(view: ModalView) => viewRoom(activeRoom, view)}
                  roomId={activeRoom?.id}
                  availableDates={availableDates}
                  isFetchingAvailableDates={isFetchingAvailableDates}
                />
              ) : (
                <DetailedRoomDesktop
                  provider={activeRoom?.provider}
                  type={activeRoom?.name}
                  description={activeRoom?.description || ''}
                  roomTypeName={activeRoom?.typeName || ''}
                  images={activeRoom?.images ?? []}
                  features={activeRoom?.features || []}
                  hasCancellationHour={Boolean(!isExpedia)}
                  showRoom={(view: ModalView) => viewRoom(activeRoom, view)}
                  roomId={activeRoom?.id}
                  isFetchingAvailableDates={isFetchingAvailableDates}
                  availableDates={availableDates}
                />
              )}

              <CardContainer>
                <RoomOptionDetails
                  room={activeRoom}
                  hotelId={hotelId}
                  isExpedia={isExpedia}
                  hotelName={hotelName}
                  openDatePicker={openDatePicker}
                  nightCount={nightCount}
                  startDate={startDate}
                  endDate={endDate}
                  hotelRoomList={hotelRoomList}
                  selectedTab={selectedTab}
                  selectedVariations={selectedVariations}
                  handleSelectVariation={handleSelectVariation}
                  selectedTabPackage={selectedTabPackage}
                  selectedDepatureFlight={selectedDepatureFlight}
                  selectedReturnFlight={selectedReturnFlight}
                  selectedDepatureTransfer={selectedDepatureTransfer}
                  selectedReturnTransfer={selectedReturnTransfer}
                  adultCount={adultCount}
                  childCount={childCount}
                  availableDates={availableDates}
                />
              </CardContainer>
            </Wrapper>
          </>
        )}
      </ShowMoreCard>
      {isPriceSheetVisible ? (
        <PriceSheet
          currency={getCurrencySymbol(Currencies.TL)}
          hotelId={hotelId}
          hotelName={hotelName}
          onCancel={onClosePriceSheet}
        />
      ) : null}
    </ContainerRoomDetail>
  );
};

export default RoomOptions;
